/**
 * @fileoverview ShadCN table primitives for consistent table styling.
 * @module components/ui/table
 */

import * as React from 'react'
import { cn } from '@/shared/utils/cn'

/**
 * Table container component with responsive wrapper.
 *
 * @component
 * @example
 * ```tsx
 * <Table>
 *   <TableHeader>...</TableHeader>
 *   <TableBody>...</TableBody>
 * </Table>
 * ```
 */
function Table({ className, ...props }: React.ComponentProps<'table'>): React.ReactElement {
  return (
    <div data-slot="table-container" className="relative w-full overflow-x-hidden">
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-sm text-foreground', className)}
        {...props}
      />
    </div>
  )
}

/**
 * Table header component.
 */
function TableHeader({ className, ...props }: React.ComponentProps<'thead'>): React.ReactElement {
  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr]:border-b [&_tr]:border-border', className)}
      {...props}
    />
  )
}

/**
 * Table body component.
 */
function TableBody({ className, ...props }: React.ComponentProps<'tbody'>): React.ReactElement {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  )
}

/**
 * Table footer component.
 */
function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>): React.ReactElement {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'bg-muted/50 border-t border-border font-medium [&>tr]:last:border-b-0',
        className
      )}
      {...props}
    />
  )
}

/**
 * Table row component with hover states.
 */
function TableRow({ className, ...props }: React.ComponentProps<'tr'>): React.ReactElement {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'hover:bg-muted/50 data-[state=selected]:bg-muted border-b border-border transition-colors',
        className
      )}
      {...props}
    />
  )
}

/**
 * Table header cell component.
 */
function TableHead({ className, ...props }: React.ComponentProps<'th'>): React.ReactElement {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'text-muted-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className
      )}
      {...props}
    />
  )
}

/**
 * Table data cell component.
 */
function TableCell({ className, ...props }: React.ComponentProps<'td'>): React.ReactElement {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'text-foreground p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className
      )}
      {...props}
    />
  )
}

/**
 * Table caption component.
 */
function TableCaption({
  className,
  ...props
}: React.ComponentProps<'caption'>): React.ReactElement {
  return (
    <caption
      data-slot="table-caption"
      className={cn('text-muted-foreground mt-4 text-sm', className)}
      {...props}
    />
  )
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption }
